/*
 * Copyright (c) 2021-2024. StockAnywhere CC
 * StockAnywhere - https://www.stockanywhere.io
 * View license agreement and terms of use at https://www.stockanywhere.io/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-06-30
 */

import React, { useEffect, useState } from "react";
import { Button , Modal } from "react-bootstrap";
import { useAuth } from "../../../contexts/AuthContext";
import { useQueryListContext } from "./QueryListContext";
import { DynamicObject, IQuery, IQueryColumnDetails, IQueryListModal, CastType } from "../Interfaces";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle } from "lucide-react";
import { formatDateFull } from "../utils/AppUtil";

enum IntegrationStatus {
    SENT = '#d4a53f', // Orange
    SUCCESS = '#d4a53f', // Green
    CLOSED = '#3ec76b', // Green
    TO_CLOSE = '#d4a53f', // Orange
    PROCESSED = '#3ec76b', // Green
    TO_SEND = '#d4a53f', // Orange
    PICKING = '#d4a53f', // Orange
    DISPATCHED = '#d4a53f', // Orange
    ARRIVED = '#d4a53f', // Orange
    TO_PROCESS = '#d4a53f', // Orange
    ERROR = '#bf3636', // Red
}

type StatusColor = {
    backgroundColor: string;
    color: string;
};

export const ListRecords: React.FC<{ columnDetails: IQueryColumnDetails[], 
                                     dataRecords: DynamicObject[],
                                     errorCategoryName?: string,
                                     tableName?:string
                                      }> = (props) => {

    const navigate = useNavigate(); // hook for navigation
   
    const {ctxData, getData, setData} = useQueryListContext();
    const {fetchData, token}  = useAuth();

    //console.log(`This is the modal config details `, props?.modalConfig);
    const modalData = getData("modal");

    //testing with modal 
    const queryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedRecordTitle, setSelectedRecord] = useState<string | null>(null); 
    const [layout, setLayout] = useState<JSX.Element | null>(null);

  
    let recordCount = 0;
    const recordElements: React.ReactElement[] = [];


    const getStatusColor = (status: string, tableName?: string): StatusColor | {} => {
        if (status === 'SUCCESS' && tableName === 'sa_m24l_item') {
            return { backgroundColor: '#3ec76b', color: 'white' }; // Override for specific case
        }
        if (IntegrationStatus[status as keyof typeof IntegrationStatus]) {
            return { backgroundColor: IntegrationStatus[status as keyof typeof IntegrationStatus], color: 'white' };
        }
        return {};
   };

    //console.log(`This is the record column details`, props.dataRecords);

    for(const recKey in props.dataRecords) {
        const record: DynamicObject = props.dataRecords[recKey];

        let columnCount = 0;
        const columnElements: React.ReactElement[] = [];
        const recordColumns = Object.entries(record);

        let primaryKey: any = null;
        let primaryKeyColumn: any = null;

        const orginalQueryState: IQuery = getData("queryState");
        const editTableName: string = orginalQueryState.frontEndOnly === null ? '' : orginalQueryState.frontEndOnly.editTableName;

        let referenceValue: any = null; // Capture reference value
        const recordId = record.id; // Store ID to use in image click logic


        // Data
        props.columnDetails.forEach((column: IQueryColumnDetails) => {

            recordColumns.forEach(([recordColumn, recordValue]) => {
                if(column.fullColumnName === recordColumn){

                    console.log(`This is the recordColumn`, recordColumn);
                     
                     // Store reference value without rendering it
                    if (recordColumn === "notifications.reference") {
                        referenceValue = recordValue;
                        return; 
                    }

                    // Skip rendering 'id' column always
                    if (recordColumn === "id") {
                        return;
                    }

                   
            
                    // Define the background style based on the integration_status 'nowrap'
                    let tdStyle: React.CSSProperties = { whiteSpace: 'nowrap', border: '1px solid #deebed',  padding: '4px 8px', fontSize: '0.75rem' };

                    // Check for 'status' or 'integration_status' and apply corresponding color
                    const status = (recordColumn === 'integration_status' || recordColumn === 'status') ? String(recordValue) : null;
                    
                   if (status) {
                        const statusColor = getStatusColor(status, props?.tableName);
                        tdStyle = { ...tdStyle, ...statusColor, textAlign: 'center', verticalAlign: 'middle' };
                    }
                     // Check if recordColumn is 'doc_num' and handle onClick to open modal
                     const handleFetchRecordDetailClick = (selectedRecord: string) => {
                        if(selectedRecord){
                            setSelectedRecord(`${selectedRecord}`);
                            fetchRecordDetail(selectedRecord);
                        }
                     };

                    // TODO
                    if(recordColumn === 'asset') {
                        columnElements.push(
                            <td key={columnCount++} style={tdStyle}><img width="100px" src={recordValue as string}></img></td>
                        );


                    } else if (recordColumn === modalData?.modalProptBy && modalData?.modalHasViewing) {
                            columnElements.push(
                                <td key={columnCount++} style={{ whiteSpace: 'normal', border: '1px solid #deebed', textDecoration: 'underline'}} onClick={() => handleFetchRecordDetailClick(recordValue as string)}>
                                    {formatColumnValue(column, recordValue)}
                                </td>
                            );
                    }else if (recordColumn === 'comments') {
                        columnElements.push(
                            <td
                                key={columnCount++}
                                style={{
                                    ...tdStyle,
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                                title={String(recordValue)}
                            >
                                {String(recordValue)}
                            </td>
                        );
                    }else if (recordColumn === 'opts' && typeof recordValue === 'object' && recordValue !== null) {
                        columnElements.push(
                            <td key={columnCount++} style={tdStyle}>
                                {Object.entries(recordValue).map(([key, value]) => {
                                    const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
                                    return (
                                        <div key={key} style={{ fontSize: '0.8rem' }}>
                                            <strong>{capitalizedKey}:</strong> {String(value)}
                                        </div>
                                    );
                                })}
                            </td>
                        );
                    } else if (recordColumn === 'assets.url' && typeof recordValue === 'string') {
                          const hasValidUrl = recordValue.startsWith('http');
                          const isClickable = modalData?.tableName === 'sa_item' && !!recordId; // Condition to enable click

                            columnElements.push(
                                <td key={columnCount++} style={tdStyle}>
                                    <div
                                        className="table-image-container"
                                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                                        onClick={() => {
                                            if (isClickable) {
                                                handleFetchRecordDetailClick(String(recordId));
                                            }
                                        }}
                                    >
                                        {hasValidUrl ? (
                                            <img src={recordValue} alt="Image" />
                                        ) : (
                                            <span style={{ fontSize: '0.7rem', color: '#999' }}>No Image</span>
                                        )}
                                    </div>
                                </td>
                            );
                    } else {
                        let displayValue = recordValue;

                        if (
                            (recordColumn === 'created' || recordColumn === 'updated')
                        ) {
                            displayValue =  formatDateFull(String(recordValue)); // Call your formatting function here
                        }

                        columnElements.push(
                            <td key={columnCount++} style={tdStyle} title={String(displayValue)}>
                                {formatColumnValue(column, recordValue)}
                            </td>
                        );
                    }


                    if(column.tableName === editTableName && column.isPk === true){
                        if(primaryKey === null) {
                            primaryKey = recordValue;
                            primaryKeyColumn = column.columnName;
                        } else {
                            primaryKey += "|" + recordValue;
                            primaryKeyColumn += "|" + column.columnName;
                        }
                    }                    
                }
            });
        });

    
        const recordIdValue = record[modalData?.modalProptBy ?? ''];
        const numericRefValue = Number(referenceValue);
        const statusValue = record["status"] ?? record["integration_status"];
        const shouldShowErrorButton = modalData?.modalHasViewing && numericRefValue > 0 && statusValue !== "CLOSED";

       const updatedAtValue = record["updated"];
       const createdAtValue = record["created"];

        const baseDateStr = typeof updatedAtValue === "string" && updatedAtValue.trim()
            ? updatedAtValue
            : typeof createdAtValue === "string" && createdAtValue.trim()
            ? createdAtValue
            : null;

        const baseDate = baseDateStr ? new Date(baseDateStr).getTime() : null;

        const isStaleUpdate = modalData?.modalHasViewing &&
            baseDate !== null &&
            statusValue !== "CLOSED" &&
            Date.now() - baseDate > 12 * 60 * 60 * 1000; // 12 hours

            
        recordElements.push(
            <tr id={primaryKey} key={recordCount++}>
                {columnElements}
                <td className="sa-record-control" style={{ border: '1px solid #deebed', height: '45px', fontSize: '0.55rem', padding: '0 5px'}}> { /* border: '1px solid #deebed', padding: '4px 8px', fontSize: '0.75rem' */ }
                   <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {shouldShowErrorButton && recordIdValue && (
                        <button
                            className="btn btn-light position-relative"
                            title="View On Error Notifications"
                            onClick={() => {
                                navigate('/m24l/notifications-new', {
                                    state: {
                                        shipment_num: recordIdValue as string,
                                        category: props?.errorCategoryName ?? ' '
                                    }
                                });
                            }}
                            style={{ width: '32px', height: '32px', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', border:'1px solid #dbdad9' }}
                        >
                            <span className="position-absolute top-0 end-0" style={{ fontSize: "0.60rem" , paddingRight: '2px', color: '#f7633e', fontWeight:'600' }}>
                                {numericRefValue}
                            </span>
                            <Bell style={{ width: "15px", height: "15px", color:'#343a40', position: 'absolute', top: '60%', transform: 'translateY(-6px)' }} />
                        </button>
                    )}

                    {isStaleUpdate && (
                        <button
                            className="btn btn-light"
                            title="Last update was over 12 hours ago"
                            style={{ width: '32px', height: '32px', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', border: '1px solid #dbdad9' }}
                        >
                            <AlertTriangle color="#4d1302" size={16} />
                        </button>
                    )}
                  </div>
                </td>
            </tr>
        );
    }


    const fetchRecordDetail = (record: string) => {
    
        if(modalData){

            setLayout(null);
            
            if(modalData.tableName === "sa_m24l_return_request"){
                const updateQueryState = modalData.queryData;
                const whereClause = updateQueryState.where.find((condition: { fullColumnName: string; }) => condition.fullColumnName === modalData.modalProptBy);
                if (whereClause) whereClause.value = record;
            
                fetchData(queryUrl, {method:"POST", body: JSON.stringify(updateQueryState)}, processRecordDetail);
               
            }

            if(modalData.tableName === "sa_m24l_stock_movement_in"){
                const updateQueryState = modalData.queryData;
                const whereClause = updateQueryState.where.find((condition: { fullColumnName: string; }) => condition.fullColumnName === modalData.modalProptBy);
                if (whereClause) whereClause.value = record;
            
                fetchData(queryUrl, {method:"POST", body: JSON.stringify(updateQueryState)}, processRecordDetail);
               
            }

            if(modalData.tableName === "sa_m24l_stock_movement_out"){
                const updateQueryState = modalData.queryData;
                const whereClause = updateQueryState.where.find((condition: { fullColumnName: string; }) => condition.fullColumnName === modalData.modalProptBy);
                if (whereClause) whereClause.value = record;
            
                fetchData(queryUrl, {method:"POST", body: JSON.stringify(updateQueryState)}, processRecordDetail);
                
            }

            if(modalData.tableName === "sa_m24l_pick_list"){
                const updateQueryState = modalData.queryData;
                const whereClause = updateQueryState.where.find((condition: { fullColumnName: string; }) => condition.fullColumnName === modalData.modalProptBy);
                if (whereClause) whereClause.value = record;
            
                fetchData(queryUrl, {method:"POST", body: JSON.stringify(updateQueryState)}, processRecordDetail);
               
            }


            if(modalData.tableName === "sa_item"){
                const updateQueryState = modalData.queryData;
                const whereClause = updateQueryState.where.find((condition: { fullColumnName: string; }) => condition.fullColumnName === "item_id");
                if (whereClause) whereClause.value = record;
            
                fetchData(queryUrl, {method:"POST", body: JSON.stringify(updateQueryState)}, processRecordDetail);
                
            }
        }
    }


    const processRecordDetail = (result: any) => {
        //console.log(result);
        const builtLayout = modalData?.processData(result);
        setLayout(builtLayout);
        setShowModal(true); 
    } 



    return(
        <>
        <tbody className="table-group-divider">
            {recordElements}
        </tbody>
 
                        
         {layout && (
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{`${modalData?.modalTitle} ${selectedRecordTitle}`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                     <h5 className=" text-uppercase text-secondary fw-bolder pb-1">
                       {modalData?.modalSubtitle}
                     </h5>
                    {layout}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
         )}
        </>
    );
}

const formatColumnValue = (columnDetail: IQueryColumnDetails, value: any): any => {
    if (value === null || value === undefined) {
        return ''; // Return blank for null/undefined
    }else if(columnDetail.datatype === "BOOLEAN") {
        return String(value);
    }else if(columnDetail.datatype === "DATE") {
        return formatDateFull(value); // formatDate(value as string)
    } else if(columnDetail.datatype === "INTEGER") {
        return Number(value);
    } else if(columnDetail.datatype === "NUMERIC") {
        return Number(value);
    } else if(columnDetail.datatype === "STRING") {
        return String(value);
    } else if(columnDetail.datatype === "UUID") {
        return String(value);
    } else if(columnDetail.datatype === "TIMESTAMP") {
      return formatDateFull(value); // formatDate(value as string)
    }
}

function formatDate(dateString: string): string | null {
    if(dateString){
        const theDate = new Date(dateString);
        return theDate.toLocaleDateString().replaceAll("\/", "-") + " " + theDate.toLocaleTimeString();
    }
    return null;
}


